import React, { useMemo } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { NsKey } from 'enums/NsKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { Stepper, StepperStep, StepperStepStatus } from 'features/subscription-process/navigation/Steps/Stepper';
import { GroupContractProcessProvider } from './contexts/GroupContractProcessProvider';
import { useGroupContractProcess } from './contexts/GroupContractProcessContext';
import { GroupContractStepIdentifier, StepStatus } from './types/enums';
import { getTranslationKeyForStep } from './utils/getRouteByStep';

const STATUS_MAP: Record<StepStatus, StepperStepStatus> = {
  [StepStatus.Pending]: 'pending',
  [StepStatus.Current]: 'current',
  [StepStatus.Completed]: 'completed',
};

const ProcessStepper: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.Contract]);
  const { process, buildStepRoute } = useGroupContractProcess();
  const navigate = useNavigate();

  const stepperSteps = useMemo<StepperStep[]>(() => {
    if (!process) return [];
    // Mirror du parcours individuel (`SubscriptionProcessWrapper.isStepDisabled`) :
    // une étape est non navigable si le process est terminé (contrat signé) OU si
    // c'est une étape encore à venir (`Pending`).
    return process.steps.map(step => {
      const disabled = process.isProcessCompleted || step.status === StepStatus.Pending;
      return {
        key: step.identifier,
        label: t(getTranslationKeyForStep(step.identifier as GroupContractStepIdentifier)),
        status: STATUS_MAP[step.status],
        onClick: disabled ? undefined : () => navigate(buildStepRoute(step.identifier)),
      };
    });
  }, [process, t, navigate, buildStepRoute]);

  if (!process) return null;
  return <Stepper steps={stepperSteps} />;
};

const GroupContractProcessInner: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.Contract]);
  const { process, contractIdentifier, isLoading, error } = useGroupContractProcess();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-gray-500">
        {t('GROUP_CONTRACT_PROCESS.WRAPPER.LOADING')}
      </div>
    );
  }

  if (error || !process) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-red-500">
        {error || t('GROUP_CONTRACT_PROCESS.WRAPPER.ERROR')}
      </div>
    );
  }

  return (
    <div className="w-full mt-8">
      <div className="mb-6 px-4 text-center">
        <h1 className="text-2xl font-semibold text-zinc-800 mb-2">
          {process.type === 'CADRE'
            ? t('GROUP_CONTRACT_PROCESS.WRAPPER.MASTER_CONTRACT_TITLE')
            : t('GROUP_CONTRACT_PROCESS.WRAPPER.GROUP_CONTRACT_TITLE')}
        </h1>
        <div className="flex justify-center font-mono gap-2">
          <p className="font-bold text-zinc-800">
            {t('GROUP_CONTRACT_PROCESS.WRAPPER.CONTRACT_NUMBER')}
          </p>
          <p className="text-gray-500">
            {contractIdentifier ?? `#${process.groupContractId}`}
          </p>
        </div>
      </div>
      <ProcessStepper />
      <div className="pt-12 px-4 max-w-5xl mx-auto">
        <Outlet />
      </div>
    </div>
  );
};

export const GroupContractProcessWrapper: React.FC = () => {
  const { t } = useDynamicTranslation([NsKey.Contract]);
  const { groupContractId } = useParams<{ groupContractId: string }>();

  if (!groupContractId) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-red-500">
        {t('GROUP_CONTRACT_PROCESS.WRAPPER.MISSING_ID')}
      </div>
    );
  }

  return (
    <GroupContractProcessProvider groupContractId={groupContractId}>
      <GroupContractProcessInner />
    </GroupContractProcessProvider>
  );
};
